import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PlanLayout from "./components/PlanLayout";
import Loader from "../../Component/Loader/Loader";
import { inr } from "../../utils/staticData";
import useMetaPricing, { CATS, CatId } from "./components/useMetaPricing";

const QUICK_QTY = [1000, 10000, 50000, 100000];

// Message cost estimate for any category / volume / plan, from the live Meta rates + plan markups
const CostCalculator = () => {
  const { loading, plans, base, markups } = useMetaPricing();
  const [cat, setCat] = useState<CatId>("marketing");
  const [qtyStr, setQtyStr] = useState("10000");
  const [planId, setPlanId] = useState("");
  const [gst, setGst] = useState(true);

  useEffect(() => {
    if (!planId && plans.length) setPlanId(plans[0]._id);
  }, [plans, planId]);

  const c = CATS.find(x => x.id === cat)!;
  const qty = +qtyStr.replace(/,/g, "") || 0;
  const gstMul = gst ? 1.18 : 1;
  const unit = base(c.key);
  const metaCost = unit * qty;
  const pctOf = (id: string) => markups[id]?.[cat] || 0;
  const totalFor = (id: string) => metaCost * (1 + pctOf(id) / 100) * gstMul;

  const plan = plans.find(p => p._id === planId);
  const pct = pctOf(planId);
  const client = metaCost * (1 + pct / 100);
  const total = client * gstMul;
  const perMsg = unit * (1 + pct / 100) * gstMul;

  const copyEstimate = () => {
    const text = [
      `WhatsApp message estimate — ${plan?.ppm_plan_title || ""} plan`,
      `India · ${c.name} · ${qty.toLocaleString("en-IN")} messages`,
      `Per message: ₹${perMsg.toFixed(4)}${gst ? " (incl. 18% GST)" : ""}`,
      `Total wallet debit: ${inr(total)}`,
    ].join("\n");
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Estimate copied to clipboard"))
      .catch(() => toast.error("Could not copy to clipboard"));
  };

  return (
    <>
      {loading && <Loader />}
      <PlanLayout actions={
        <button type="button" className="cx-btn cx-btn-light" onClick={copyEstimate} disabled={!plan}><i className="ri-file-copy-line"></i>Copy estimate</button>
      }>
        <div className="cx-grid g12">
          <div className="cx-card">
            <div className="cx-card-h"><h6>Inputs</h6></div>
            <div className="cx-card-b">
              <div className="cx-fgrid" style={{ gridTemplateColumns: "1fr" }}>
                <div>
                  <label className="cx-label">Country</label>
                  <select className="cx-select" disabled><option>India · ₹</option></select>
                </div>
                <div>
                  <label className="cx-label">Category</label>
                  <select className="cx-select" value={cat} onChange={(e) => setCat(e.target.value as CatId)}>
                    {CATS.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="cx-label">Messages</label>
                  <input className="cx-input mono" inputMode="numeric" value={qtyStr} onChange={(e) => setQtyStr(e.target.value)} />
                  <div className="cx-chips" style={{ marginTop: 8 }}>
                    {QUICK_QTY.map(n => (
                      <button type="button" key={n} className={`cx-chip ${qty === n ? "on" : ""}`} onClick={() => setQtyStr(String(n))}>{n.toLocaleString("en-IN")}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="cx-label">Client plan</label>
                  <select className="cx-select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                    {plans.map(p => <option key={p._id} value={p._id}>{p.ppm_plan_title}</option>)}
                  </select>
                </div>
                <div className="cx-togrow">
                  <label className="cx-switch"><input type="checkbox" checked={gst} onChange={(e) => setGst(e.target.checked)} /><span></span></label>
                  <span className="fs12">Include GST 18%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="cx-card">
            <div className="cx-card-h">
              <h6>Estimate</h6>
              <span className="sub">India · {c.name} · {qty.toLocaleString("en-IN")} msgs</span>
            </div>
            <div className="cx-card-b">
              <div className="cx-grid g2">
                <div className="cx-calc-hero">
                  <small>Client wallet debit</small>
                  <b>{inr(total)}</b>
                  <span>₹{perMsg.toFixed(4)} per message</span>
                </div>
                <div>
                  <div className="cx-calc-row"><span>Meta cost</span><b className="mono">{inr(metaCost)}</b></div>
                  <div className="cx-calc-row"><span>Markup ({+pct.toFixed(2)}%)</span><b className="mono up">+{inr(client - metaCost)}</b></div>
                  {gst && <div className="cx-calc-row"><span>GST 18%</span><b className="mono">{inr(client * 0.18)}</b></div>}
                  <div className="cx-calc-row tot"><span>Your margin</span><b className="mono up">{inr(client - metaCost)}</b></div>
                </div>
              </div>

              <h6 className="cx-h6">Same usage on every plan</h6>
              <div className="cx-tbl-wrap">
                <table className="cx-tbl cx-cmp-tbl">
                  <thead><tr><th>Plan</th><th>Markup</th><th>Per message</th><th>Total</th><th>vs selected</th></tr></thead>
                  <tbody>
                    {plans.map(p => {
                      const t = totalFor(p._id);
                      const diff = t - total;
                      return (
                        <tr key={p._id} className={p._id === planId ? "sel" : ""}>
                          <td>{p.ppm_plan_title}</td>
                          <td className="mono">{+pctOf(p._id).toFixed(2)}%</td>
                          <td className="mono">₹{(unit * (1 + pctOf(p._id) / 100) * gstMul).toFixed(4)}</td>
                          <td className="mono">{inr(t)}</td>
                          <td className={`mono ${diff < 0 ? "up" : diff > 0 ? "down" : "muted"}`}>
                            {Math.abs(diff) < 0.5 ? "—" : `${diff > 0 ? "+" : "−"}${inr(Math.abs(diff))}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </PlanLayout>
    </>
  );
};

export default CostCalculator;
